import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  useAssistant,
  type AssistantPageContext,
  type UseAssistant,
} from "../components/assistant/useAssistant";

/**
 * "The object currently open" (ticket 21, §D14).
 *
 * The URL alone is not the context: the provider's order drawer keeps its order
 * in component state (`sheetOrderId`), so «и что с ним дальше?» on the busiest
 * screen in the app would have no referent. A page publishes what it has open;
 * pages that publish nothing simply contribute their route.
 *
 * This is **never an authorization input** — the id it carries is re-authorized
 * by the tool layer exactly like an id the model named.
 *
 * Both providers here are mounted in `AppShell`, **above** the app's Suspense
 * boundary, and deliberately not inside `DashboardLayout`. Two reasons: a page
 * can only publish into a context that sits above it, and the conversation
 * belongs to the *tab* — a remount of the routed subtree (which the shell's
 * Suspense fallback really does cause) must not throw away what the user has
 * asked. Only the widget itself lives in the cabinet layout, so the assistant
 * still never appears on a public page.
 */
export interface AssistantObject {
  type: "order" | "contract";
  id: string;
  /** Shown in the panel's context line. A title, never a raw route. */
  title: string;
}

interface AssistantContextValue {
  object: AssistantObject | null;
  publish: (object: AssistantObject | null) => void;
}

const AssistantContext = createContext<AssistantContextValue | null>(null);

export function AssistantProvider({ children }: { children: React.ReactNode }) {
  const [object, setObject] = useState<AssistantObject | null>(null);
  const value = useMemo<AssistantContextValue>(
    () => ({ object, publish: setObject }),
    [object]
  );
  return <AssistantContext.Provider value={value}>{children}</AssistantContext.Provider>;
}

/** Read-only access for the widget. Null outside the provider, which is what a
 *  page rendered outside the cabinet layout gets. */
export function useAssistantObject(): AssistantObject | null {
  return useContext(AssistantContext)?.object ?? null;
}

/**
 * Publish for as long as the caller is mounted, and clear on unmount.
 *
 * The identity of `object` is compared by value, not by reference, so a page
 * that rebuilds the literal on every render does not publish on every render.
 */
export function usePublishAssistantObject(object: AssistantObject | null): void {
  const context = useContext(AssistantContext);
  const publish = context?.publish;
  const key = object ? `${object.type}|${object.id}|${object.title}` : "";

  useEffect(() => {
    if (!publish) return;
    if (!key) {
      publish(null);
      return;
    }
    const [type, id, ...rest] = key.split("|");
    publish({ type: type as AssistantObject["type"], id, title: rest.join("|") });
    return () => publish(null);
  }, [publish, key]);
}

/**
 * The address the assistant is told. An opened invoice (ticket 25) is cut back
 * to its section: the assistant has no tool for those records, so the id in the
 * address would be the only part of one to leave the browser. The server cuts
 * it again; this keeps it from being sent at all.
 */
export function assistantRoute(pathname: string): string {
  return pathname.startsWith("/extra-services/invoices/") ? "/extra-services/invoices" : pathname;
}

/**
 * The conversation itself, held above the Suspense boundary (see above). The
 * page context it needs is read from the provider next to it, so the panel can
 * be a pure consumer.
 */
const AssistantConversationContext = createContext<UseAssistant | null>(null);

export function AssistantConversationProvider({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const object = useAssistantObject();
  const pageContext = useMemo<AssistantPageContext>(
    () => ({
      route: assistantRoute(location.pathname),
      ...(object ? { objectType: object.type, objectId: object.id } : {}),
    }),
    [location.pathname, object]
  );
  const conversation = useAssistant(pageContext);
  return (
    <AssistantConversationContext.Provider value={conversation}>
      {children}
    </AssistantConversationContext.Provider>
  );
}

export function useAssistantConversation(): UseAssistant {
  const context = useContext(AssistantConversationContext);
  if (!context) {
    throw new Error("useAssistantConversation must be used within AssistantConversationProvider");
  }
  return context;
}
